export interface YawnEvidenceContext {
  jawOpen: number;
  mouthStretch: number;
  eyeSquint: number;
  eyeWide: number;
  browRaise: number;
  browDown: number;
  avgMar: number;
  avgEar: number;
  yawnThreshold: number;
  yawnMarThreshold: number;
  yawnEarThreshold: number;
}

export interface YawnEvidenceProvider {
  readonly id: 'geometry' | 'blendshape-fallback';
  computeYawnScore(context: YawnEvidenceContext): number;
  getVoteThreshold(yawnThreshold: number): number;
  hasMarEvidence(context: YawnEvidenceContext): boolean;
  hasEarEvidence(context: YawnEvidenceContext): boolean;
  shouldIncrementHold(context: YawnEvidenceContext): boolean;
  isSurpriseLikeOpenMouth(context: YawnEvidenceContext): boolean;
}

class GeometryYawnEvidenceProvider implements YawnEvidenceProvider {
  readonly id = 'geometry' as const;

  computeYawnScore(context: YawnEvidenceContext): number {
    // Require minimum eye squint — mouth open alone is not a yawn
    if (context.eyeSquint < 0.10) return 0;
    return (context.jawOpen * 0.70) + (context.mouthStretch * 0.45) + (context.eyeSquint * 0.30);
  }

  getVoteThreshold(yawnThreshold: number): number {
    return yawnThreshold;
  }

  hasMarEvidence(context: YawnEvidenceContext): boolean {
    return context.avgMar >= context.yawnMarThreshold * 0.82;
  }

  hasEarEvidence(context: YawnEvidenceContext): boolean {
    return context.avgEar <= context.yawnEarThreshold;
  }

  shouldIncrementHold(context: YawnEvidenceContext): boolean {
    return context.avgMar >= context.yawnMarThreshold && context.avgEar <= context.yawnEarThreshold;
  }

  isSurpriseLikeOpenMouth(context: YawnEvidenceContext): boolean {
    return context.avgMar >= context.yawnMarThreshold * 0.92
      && context.eyeSquint < 0.16
      && context.browDown < 0.10
      && context.mouthStretch < 0.12
      && context.jawOpen > 0.24;
  }
}

class BlendshapeFallbackYawnEvidenceProvider implements YawnEvidenceProvider {
  readonly id = 'blendshape-fallback' as const;

  computeYawnScore(context: YawnEvidenceContext): number {
    return (context.jawOpen * 0.76) + (context.mouthStretch * 0.42) + (context.eyeSquint * 0.26);
  }

  getVoteThreshold(yawnThreshold: number): number {
    return yawnThreshold * 0.88;
  }

  hasMarEvidence(context: YawnEvidenceContext): boolean {
    return context.jawOpen >= 0.34 || this.computeYawnScore(context) >= context.yawnThreshold * 0.95;
  }

  hasEarEvidence(_context: YawnEvidenceContext): boolean {
    return true;
  }

  shouldIncrementHold(context: YawnEvidenceContext): boolean {
    const score = this.computeYawnScore(context);
    return context.jawOpen >= 0.34 && (score >= context.yawnThreshold * 0.90 || context.mouthStretch >= 0.08);
  }

  isSurpriseLikeOpenMouth(context: YawnEvidenceContext): boolean {
    return context.browRaise > 0.22
      && context.eyeWide > 0.16
      && context.eyeSquint < 0.14
      && context.jawOpen > 0.26;
  }
}

const GEOMETRY_PROVIDER = new GeometryYawnEvidenceProvider();
const FALLBACK_PROVIDER = new BlendshapeFallbackYawnEvidenceProvider();

export function resolveYawnEvidenceProvider(hasReliableGeometry: boolean): YawnEvidenceProvider {
  return hasReliableGeometry ? GEOMETRY_PROVIDER : FALLBACK_PROVIDER;
}
